import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import "./Modal.scss";

import UniversalButton from "components/uiComponents/UniversalButton";
import Select from "components/uiComponents/Select";
import UniversalInput from "components/uiComponents/UniversalInput";
import DatePicker from "components/uiComponents/Datepicker";

import ReactSelect from 'react-select';

import { setCurrentTableFromModelCellAC } from "store/actions/table";
import { getParametersOfSelectedElements } from "store/actions/viewer";

import { displayType } from "../../uiComponents/CustomBodyCellV2/model/types";
import {
  isTextOrNumberType,
  isBoolType,
  isDateType,
  isEnumType,
  isMultiEnumType,
  isNumberType,
  getReactSelectStyles,
  booleanOprions,
} from "utils/valueTypeHelpers";

const SetCustomParameterModal = ({ closeModal, optionToEdit = null }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getParametersOfSelectedElements())
  }, [dispatch])

  const { parametersOfSelection, adskViewerSelection } = useSelector((state) => state.viewer);

  const [value, setValue] = useState(optionToEdit ? optionToEdit.selectedValue : null);
  const [currentOption, setCurrentOption] = useState(optionToEdit || null);
  const [multiplechoiceSelectedValue, setMultiplechoiceSelectedValue] = useState([]);
  const [customBoolValue, setCustomBoolValue] = useState(null);
  const [customDate, setCustomDate] = useState(null);



  useEffect(() => {
    if (currentOption && adskViewerSelection.dbIdArray.length === 1) {
      const type = currentOption.type;
      
      if (isTextOrNumberType(type)) {
        setValue(currentOption.selectedValue);
      } else if (isBoolType(type)) {
        setCustomBoolValue(currentOption.selectedValue);
      } else if (isDateType(type)) {
        setCustomDate(currentOption.selectedValue);
      } else if (isEnumType(type)) {
        setMultiplechoiceSelectedValue(currentOption.selectedValue);
      }
    }
  }, [currentOption, adskViewerSelection?.dbIdArray?.length]);

  // Extract custom parameters from parametersOfSelection
  const getCustomParametersFromSelection = (parametersOfSelection) => {
    if (!parametersOfSelection) return [];
    const resultMap = {};
    for (const elemId of Object.keys(parametersOfSelection)) {
      const elemCustomParamsList = parametersOfSelection[elemId].customProperties;
      for (const customParam of elemCustomParamsList) {
        const paramName = customParam.name;
        const currentValue = customParam.selectedValue;
        if (!resultMap[paramName]) {
          resultMap[paramName] = { ...customParam };
          continue;
        }
        const existingValue = resultMap[paramName].selectedValue;
        if (existingValue !== "Varies" && existingValue !== currentValue) {
          resultMap[paramName].selectedValue = "Varies";
        }
      }
    }
    return Object.values(resultMap);
  };

  const customPropertiesValues = useMemo(() => {
    return getCustomParametersFromSelection(parametersOfSelection);
  }, [parametersOfSelection]);

  const options = useMemo(() => {
    return customPropertiesValues?.map((item) => ({
      key: item.name,
      label: item.name,
      value: item.name,
      values: item.values,
      type: item.type,
      selectedValue: item.selectedValue,
    })) || [];
  }, [customPropertiesValues]);


  const multiplechoiceValuesOptions = useMemo(() => {
    return currentOption?.values?.map(item => ({ label: item, value: item }))
  }, [currentOption])

  const handleSetData = () => {
    if (!currentOption) return;

    const type = currentOption.type;
    let valueToSet;

    if (isEnumType(type)) {
      valueToSet = Array.isArray(multiplechoiceSelectedValue)
        ? multiplechoiceSelectedValue.map(item => item.value).join('; ')
        : multiplechoiceSelectedValue?.label;
    } else if (isBoolType(type)) {
      valueToSet = customBoolValue?.value;
    } else if (isDateType(type)) {
      valueToSet = customDate;
    } else {
      valueToSet = value;
    }

    dispatch(setCurrentTableFromModelCellAC(currentOption.value, valueToSet)).then(() => {
      dispatch(getParametersOfSelectedElements());
    });
    
    closeModal();
  };

  const reactSelectStyles = getReactSelectStyles();

  const renderField = () => {
    if (!currentOption) {
      return (
        <UniversalInput
          value={value}
          onChangeValue={setValue}
          type="text"
          withFullWidth={true}
          disabled={true}
        />
      );
    }

    const type = currentOption.type;

    if (isTextOrNumberType(type)) {
      const inputType = isNumberType(type) ? 'number' : 'text';
      return (
        <UniversalInput
          value={value}
          onChangeValue={setValue}
          type={inputType}
          withFullWidth={true}
        />
      );
    }

    if (isBoolType(type)) {
      return (
        <ReactSelect
          value={customBoolValue}
          onChange={(value) => setCustomBoolValue(value)}
          options={booleanOprions}
          isSearchable={false}
          styles={reactSelectStyles}
          classNamePrefix="react-select"
        />
      );
    }

    if (isDateType(type)) {
      return (
        <DatePicker
          value={customDate ? new Date(customDate) : new Date()}
          onChangeValue={(value) => setCustomDate(value)}
        />
      );
    }

    if (isEnumType(type)) {
      return (
        <ReactSelect
          value={multiplechoiceSelectedValue}
          placeholder={t('please_select_value')}
          onChange={(value) => setMultiplechoiceSelectedValue(value)}
          options={multiplechoiceValuesOptions}
          isMulti={isMultiEnumType(type)}
          isSearchable={false}
          styles={reactSelectStyles}
          classNamePrefix="react-select"
        />
      );
    }

    return (
      <UniversalInput
        value={value}
        onChangeValue={setValue}
        type={type === displayType.number ? 'number' : "text"}
        withFullWidth={true}
        disabled={!currentOption}
      />
    );
  };

  return (
    <div className="modal modal-set-data">
      <span className="modal__header">{t('set_data')}</span>

      <div className="modal__content">
        <div>
          <p className="modal__content-field-title">{t('column')}</p>
          <Select
            selectedValue={currentOption}
            setSelectedValue={setCurrentOption}
            isBold={false}
            selectSize={"md"}
            options={options}
            isSmallHeight={true}
          />
        </div>

        <div>
          <p className="modal__content-field-title">{t('value')}</p>
          {renderField()}
        </div>

      </div>

      <div className="modal__footer">
        <div className="modal__footer__controls">
          <UniversalButton
            type="button"
            label={t('cancel')}
            size="lg"
            labelColor="blue"
            labelHoverColor="blue"
            backgroundColor="white"
            backgroundHoverColor="blue-light"
            onClick={closeModal}
          />

          <UniversalButton
            type="button"
            label={t('apply')}
            size="lg"
            labelColor="white"
            labelHoverColor="white"
            backgroundColor="orange"
            backgroundHoverColor="orange-light"
            onClick={handleSetData}
          />
        </div>
      </div>
    </div>
  );
};

export default SetCustomParameterModal;
